import React, { useState, useEffect } from 'react';
import { adminService } from '../../services';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi2';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';
import './EmailTemplates.css';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    templateCode: '',
    templateName: '',
    description: '',
    channel: 'Email',
    subject: '',
    body: '',
    variables: '',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await adminService.getEmailTemplates();
      setTemplates(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load templates');
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      templateCode: '',
      templateName: '',
      description: '',
      channel: 'Email',
      subject: '',
      body: '',
      variables: '',
      isActive: true,
    });
    setFormErrors({});
    setError(null);
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      templateCode: template.templateCode,
      templateName: template.templateName,
      description: template.description || '',
      channel: template.channel || 'Email',
      subject: template.subject || '',
      body: template.body || '',
      variables: template.variables || '',
      isActive: template.isActive,
    });
    setFormErrors({});
    setError(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await adminService.deleteEmailTemplate(id);
      await loadTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete template');
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.templateCode || !formData.templateCode.trim()) {
      errors.templateCode = 'Template code is required';
    }
    
    if (!formData.templateName || !formData.templateName.trim()) {
      errors.templateName = 'Template name is required';
    }
    
    if (!formData.subject || !formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    
    if (!formData.body || !formData.body.trim()) {
      errors.body = 'Body is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFormErrors({});
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Ensure templateCode is trimmed and not empty
      const payload = {
        ...formData,
        templateCode: formData.templateCode.trim(),
        templateName: formData.templateName.trim(),
        subject: formData.subject.trim(),
        body: formData.body.trim(),
        description: formData.description?.trim() || '',
        variables: formData.variables?.trim() || '',
      };
      
      if (editingTemplate) {
        await adminService.updateEmailTemplate(editingTemplate.templateId, payload);
      } else {
        await adminService.createEmailTemplate(payload);
      }
      setShowModal(false);
      await loadTemplates();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.title || 'Failed to save template';
      const validationErrors = err.response?.data?.errors;
      
      if (validationErrors) {
        // Map backend validation errors to form errors
        const mappedErrors = {};
        if (validationErrors.TemplateCode) {
          mappedErrors.templateCode = validationErrors.TemplateCode[0] || 'Template code is required';
        }
        if (validationErrors.TemplateName) {
          mappedErrors.templateName = validationErrors.TemplateName[0] || 'Template name is required';
        }
        if (validationErrors.Subject) {
          mappedErrors.subject = validationErrors.Subject[0] || 'Subject is required';
        }
        if (validationErrors.Body) {
          mappedErrors.body = validationErrors.Body[0] || 'Body is required';
        }
        setFormErrors(mappedErrors);
      }
      setError(errorMessage);
    }
  };

  if (loading) {
    return <PageLoading message="Loading templates..." />;
  }

  return (
    <div className="email-templates-container">
      <div className="page-header">
        <h1>Email Templates</h1>
        <button className="btn-primary" onClick={handleCreate}>
          Create Template
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="templates-table-container">
        <table className="templates-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Channel</th>
              <th>Status</th>
              <th>Version</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  No templates found. Create your first template.
                </td>
              </tr>
            ) : (
              templates.map((template) => (
                <tr key={template.templateId}>
                  <td>{template.templateCode}</td>
                  <td>{template.templateName}</td>
                  <td>{template.channel || 'Email'}</td>
                  <td>
                    <span className={`status-badge ${template.isActive ? 'active' : 'inactive'}`}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{template.version || 1}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(template)}
                        title="Edit Template"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => handleDelete(template.templateId)}
                        title="Delete Template"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingTemplate ? 'Edit Template' : 'Create Template'}
        size="lg"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
              Cancel
            </button>
            <button type="submit" form="template-form" className="btn btn-primary">
              {editingTemplate ? 'Update' : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="template-form" onSubmit={handleSubmit} className="template-form">
            <div className="form-group">
              <label>Template Code *</label>
              <input
                type="text"
                value={formData.templateCode}
                onChange={(e) => {
                  setFormData({ ...formData, templateCode: e.target.value });
                  setFormErrors({ ...formErrors, templateCode: '' });
                }}
                required
                disabled={!!editingTemplate}
                placeholder="e.g., CAPA_ASSIGNED"
                className={formErrors.templateCode ? 'error' : ''}
              />
              {formErrors.templateCode && (
                <span className="error-text">{formErrors.templateCode}</span>
              )}
            </div>
            <div className="form-group">
              <label>Template Name *</label>
              <input
                type="text"
                value={formData.templateName}
                onChange={(e) => {
                  setFormData({ ...formData, templateName: e.target.value });
                  setFormErrors({ ...formErrors, templateName: '' });
                }}
                required
                className={formErrors.templateName ? 'error' : ''}
              />
              {formErrors.templateName && (
                <span className="error-text">{formErrors.templateName}</span>
              )}
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="2"
              />
            </div>
            <div className="form-group">
              <label>Channel</label>
              <select
                value={formData.channel}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
              >
                <option value="Email">Email</option>
                <option value="SMS">SMS</option>
                <option value="Push">Push</option>
              </select>
            </div>
            <div className="form-group">
              <label>Subject *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => {
                  setFormData({ ...formData, subject: e.target.value });
                  setFormErrors({ ...formErrors, subject: '' });
                }}
                required
                placeholder="Use {{VariableName}} for variables"
                className={formErrors.subject ? 'error' : ''}
              />
              {formErrors.subject && (
                <span className="error-text">{formErrors.subject}</span>
              )}
            </div>
            <div className="form-group">
              <label>Body *</label>
              <textarea
                value={formData.body}
                onChange={(e) => {
                  setFormData({ ...formData, body: e.target.value });
                  setFormErrors({ ...formErrors, body: '' });
                }}
                required
                rows="10"
                placeholder="Use {{VariableName}} for variables. HTML is supported."
                className={formErrors.body ? 'error' : ''}
              />
              {formErrors.body && (
                <span className="error-text">{formErrors.body}</span>
              )}
            </div>
            <div className="form-group">
              <label>Variables (JSON)</label>
              <textarea
                value={formData.variables}
                onChange={(e) => setFormData({ ...formData, variables: e.target.value })}
                rows="4"
                placeholder='{"UserName": "User full name", "Date": "Current date"}'
              />
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Active
              </label>
            </div>
          </form>
        </div>
      </SharedModal>
    </div>
  );
};

export default EmailTemplates;

